import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import {
  getCoreRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  type ForeignTransactionsFilter,
  useForeignTransactionsQuery,
} from "@/modules/foreignPayments/api/useForeignTransactions.ts";
import { columnLabelsMap, foreignColumns } from "@/modules/foreignPayments/TableColumnsDef.tsx";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { getCurrenciesOptions } from "@/modules/domesticPayments/utils.ts";
import {
  getFilterValue,
  getItemPerPage,
  getSortingValue,
  getVisibilityStateValue,
  setFilterValue,
  setItemPerPage,
  setVisibilityStateValue,
  sortingParser,
} from "@/lib/table.ts";
import { PageHeader } from "@/components/PageHeader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { DataTable } from "@/components/table/DataTable.tsx";
import { parseAsIndex, useQueryState } from "nuqs";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { FilterProvider, type FilterProviderOnChange } from "@/components/table/FilterProvider.tsx";
import { Export } from "@/modules/foreignPayments/Export.tsx";
import { AllBankDataProvider } from "@/dataProviders/BankDataProvider.tsx";

const FOREIGN_FILTERS_KEY = "@fin_merch_foreign_filters";
const FOREIGN_COLUMN_VISIBILITY_KEY = "@fin_merch_foreign_visible_columns";
export const FOREIGN_LOCATION_KEY = "@fin_merch_foreign_location";
export const Route = createFileRoute("/payments/foreign/")({
  component: RouteComponent,
});

function RouteComponent() {
  const title = t`Foreign payments`;
  const { activeApp, currencies } = useAppConfig();

  const [sorting, setSorting] = useQueryState<SortingState>(
    "sort",
    sortingParser.withDefault([{ id: "createdAt", desc: true }]),
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getVisibilityStateValue(FOREIGN_COLUMN_VISIBILITY_KEY),
  );

  useEffect(() => {
    setVisibilityStateValue(FOREIGN_COLUMN_VISIBILITY_KEY, columnVisibility);
  }, [columnVisibility]);

  const [filter, setFilter] = useState<ForeignTransactionsFilter>(() => {
    const stored = getFilterValue<
      ForeignTransactionsFilter & { debtorIban?: string; creditorIban?: string }
    >(FOREIGN_FILTERS_KEY);

    const { debtorIban, creditorIban, ...rest } = stored;

    return {
      ...rest,
      ...(debtorIban ? { debtorAccountIdentificationValue: debtorIban } : {}),
      ...(creditorIban ? { creditorAccountIdentificationValue: creditorIban } : {}),
    };
  });
  const onFilterChange: FilterProviderOnChange = (values) => {
    setFilter((prevState) => ({ ...prevState, ...values }));
    const nextFilter = { ...filter, ...values } as ForeignTransactionsFilter & {
      debtorIban?: string;
      creditorIban?: string;
    };
    delete nextFilter.debtorIban;
    delete nextFilter.creditorIban;
    setFilterValue(FOREIGN_FILTERS_KEY, nextFilter);
  };

  const [page, setPage] = useQueryState<number>("page", parseAsIndex.withDefault(0));
  const [perPage, setPerPage] = useState<number>(getItemPerPage());

  const pagination: PaginationState = { pageIndex: page, pageSize: perPage };
  const setPagination: OnChangeFn<PaginationState> = (value) => {
    if ("pageIndex" in value) return;

    const { pageIndex, pageSize } = value(pagination);
    void setPage(pageIndex);
    setPerPage(pageSize);
    setItemPerPage(pageSize);
  };

  const { data, isPending, isError, error } = useForeignTransactionsQuery(
    {
      pageNumber: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: getSortingValue(sorting ?? []),
      ...filter,
    },
    { merchantId: activeApp },
    { enabled: !!activeApp, placeholderData: keepPreviousData },
  );

  const columnsConfig = useMemo(() => foreignColumns(getCurrenciesOptions(currencies)), [currencies]);

  const table = useReactTable({
    data: data?.items ? data.items : [],
    columns: columnsConfig,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.pagination?.totalItemsCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: { sorting, columnVisibility, pagination },
  });

  const labels = useMemo(columnLabelsMap, []);

  return (
    <AllBankDataProvider>
      <FilterProvider value={filter} onChange={onFilterChange}>
        <PageWrapper>
          <PageHeader breadcrumbs={[{ href: "/", title }]} />
          <div className="flex flex-col grow p-5">
            {isError && (
              <FetchingError
                message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
                error={error}
              />
            )}
            {isPending && <Loader message={t`Loading foreign payments...`} />}
            {!isPending && !isError && (
              <DataTable
                table={table}
                labels={labels}
                detailRoute={"/payments/foreign/$id"}
                detailIdKey={"merchantTransactionId"}
                exportComponent={<Export table={table} filter={filter} />}
                locationKey={FOREIGN_LOCATION_KEY}
              />
            )}
          </div>
        </PageWrapper>
      </FilterProvider>
    </AllBankDataProvider>
  );
}
